import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { StarRating } from './star-rating';
import { Review } from '@/types/reviews.types';

const reviewFormSchema = z.object({
  rating: z.number().min(1, 'Rating is required').max(5, 'Rating must be between 1 and 5'),
  reviewText: z.string().max(1000, 'Review text must be less than 1000 characters').optional(),
});

type ReviewFormData = z.infer<typeof reviewFormSchema>;

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantName: string;
  existingReview?: Review | null;
  onSubmit: (data: ReviewFormData) => Promise<void>;
  isLoading?: boolean;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  restaurantName,
  existingReview,
  onSubmit,
  isLoading = false,
}) => {
  const isEditing = !!existingReview;

  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewFormSchema),
    defaultValues: {
      rating: existingReview?.rating || 0,
      reviewText: existingReview?.reviewText || '',
    },
  });

  React.useEffect(() => {
    if (existingReview) {
      form.reset({
        rating: existingReview.rating,
        reviewText: existingReview.reviewText || '',
      });
    } else {
      form.reset({
        rating: 0,
        reviewText: '',
      });
    }
  }, [existingReview, form]);

  const handleSubmit = async (data: ReviewFormData) => {
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Failed to submit review:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Review' : 'Write a Review'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isEditing ? 'Update your review for' : 'Share your experience at'} {restaurantName}
          </p>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rating</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <StarRating
                        rating={field.value}
                        interactive
                        onRatingChange={field.onChange}
                        size="lg"
                      />
                      {field.value > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {field.value} star{field.value !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reviewText"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Review (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Share your thoughts about this restaurant..."
                      className="min-h-[100px] resize-none"
                      maxLength={1000}
                      {...field}
                    />
                  </FormControl>
                  <div className="flex justify-between items-center">
                    <FormMessage />
                    <span className="text-xs text-muted-foreground">
                      {field.value?.length || 0}/1000
                    </span>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || form.watch('rating') === 0}
              >
                {isLoading ? 'Saving...' : isEditing ? 'Update Review' : 'Submit Review'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};